import { Dialog, DialogContent, DialogTitle, IconButton, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { setSelectedTier } from '@paintscout/util/builder'
import CloseIcon from '@material-ui/icons/Close'
import type { TieredPackageTierId } from 'paintscout'
import React from 'react'
import { useQuote } from '../../../../context'
import PackageComparisonCards from './PackageComparisonCards'

const useStyles = makeStyles<Theme>(
  () => ({
    // 920px wide, capped so it always fits the viewport, and scrolling its own body rather than the
    // page behind it (ENG-1542). The cards inside are a three-up row that would otherwise force the
    // dialog wider than the screen on a laptop.
    paper: {
      width: 920,
      maxWidth: '96vw',
      maxHeight: '92vh'
    },
    content: {
      // The cards bring their own padding, so the content area only needs the dialog's own gutter.
      paddingTop: 0
    }
  }),
  { name: 'PackageCompareDialog' }
)

export interface PackageCompareDialogProps {
  open: boolean
  onClose: () => void
}

/**
 * "Compare plans" — the customer's side-by-side view of every offered package, opened from the total
 * table's Switch control (ENG-1542).
 *
 * The columns are the same {@link PackageComparisonCards} the gate page uses, in its `switch`
 * variant: the current package's button reads "Selected", the others read "Switch to <name>".
 * Choosing one applies it through the surrounding quote context — persisted in the editor,
 * recomputed locally in the customer and preview views — and closes the dialog.
 */
export default function PackageCompareDialog({ open, onClose }: PackageCompareDialogProps) {
  const classes = useStyles({})
  const { quote, updateQuote } = useQuote()
  const { options } = useClientOptions()

  const handleSelect = (tierId: TieredPackageTierId) => {
    if (tierId !== quote.tieredPackages?.selectedTierId) {
      updateQuote({ quote: setSelectedTier({ quote, options, tierId }) })
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }} maxWidth={false}>
      <DialogTitle
        rightContent={
          <IconButton aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        }
      >
        Compare plans
      </DialogTitle>
      <DialogContent className={classes.content}>
        <PackageComparisonCards variant="switch" onSelectTier={handleSelect} />
      </DialogContent>
    </Dialog>
  )
}
